import { useState } from 'react'

import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { Banner } from 'slab'
import {
  useGetEmployersAcaReportingFilingEligibility,
  useGetEmployersAcaReportingNeedsAleConfirmation,
  useGetEmployersAcaReportingStatus
} from 'utils/web_api'

import ACAReportingHeader from './ACAReportingHeader'
import AleStatusConfirmation from './AleStatusConfirmation'
import NonFilingEligibleView from './NonFilingEligibleView'
import OptInForm from './OptInForm'
import StatusConfirmation from './StatusConfirmation'

interface EligibilityCheckerProps {
  planYear: number
}

export default function EligibilityChecker ({
  planYear
}: EligibilityCheckerProps) {
  const [showOptInSuccess, setShowOptInSuccess] = useState(false)
  const [showOptOutSuccess, setShowOptOutSuccess] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [formStartedFrom, setFormStartedFrom] = useState<'opted_in' | 'opted_out' | null>(null)
  const [confirmedAleStatus, setConfirmedAleStatus] = useState<boolean | null>(null)

  // Fetch filing eligibility first to determine routing
  const { data: filingData, isLoading: filingLoading } = useGetEmployersAcaReportingFilingEligibility(
    { plan_year: planYear },
    {
      query: {
        retry: false
      }
    }
  )

  const isFilingEligible = filingData?.aca_filing_enabled

  // Only fetch status for filing-eligible employers
  const { data: statusData, isLoading: statusLoading, error: statusError } = useGetEmployersAcaReportingStatus(
    { plan_year: planYear },
    {
      query: {
        retry: false, // 404 expected when no config exists yet
        enabled: !filingLoading && isFilingEligible === true
      }
    }
  )

  // Fetch ALE confirmation status for both filing and non-filing employers
  const { data: aleData, isLoading: aleLoading, error: aleError } = useGetEmployersAcaReportingNeedsAleConfirmation(
    { plan_year: planYear },
    {
      query: {
        retry: false,
        enabled: !filingLoading
      }
    }
  )

  // Only check statusLoading if the query is actually enabled
  const shouldCheckStatusLoading = !filingLoading && isFilingEligible === true

  if (filingLoading || aleLoading || (shouldCheckStatusLoading && statusLoading)) {
    return <LoadingSpinnerCentered />
  }

  const hasNoExistingConfig = (statusError as { status?: number } | null)?.status === 404
  const hasStatusError = statusError && !hasNoExistingConfig

  // Only show status error for filing-eligible employers
  if (hasStatusError && isFilingEligible === true) {
    return (
      <Banner type='critical' title='Unable to load status'>
        There was an error loading your ACA reporting status. Please try again later.
      </Banner>
    )
  }

  // Check for ALE confirmation errors (only if query was enabled)
  if (aleError && !statusError) {
    return (
      <Banner type='critical' title='Unable to load ALE confirmation status'>
        There was an error checking your ALE status. Please try again later.
      </Banner>
    )
  }

  const status = statusData?.status
  const needsAleConfirmation = aleData?.needs_confirmation && confirmedAleStatus === null

  // Non-filing-eligible employers: show ALE confirmation first, then NonFilingEligibleView
  if (isFilingEligible === false) {
    if (needsAleConfirmation) {
      return (
        <AleStatusConfirmation
          planYear={planYear}
          onConfirmed={(aleStatus) => setConfirmedAleStatus(aleStatus)}
        />
      )
    }

    return (
      <NonFilingEligibleView
        planYear={planYear}
        aleStatus={confirmedAleStatus ?? aleData?.ale_status}
      />
    )
  }

  // Filing-eligible employers: go directly to opt-in flow (OptInForm includes ALE question)

  // Show form when explicitly requested via "change mind" clicks
  if (showForm) {
    return (
      <>
        <ACAReportingHeader planYear={planYear} isFilingEligible />

        <OptInForm
          planYear={planYear}
          onOptInSuccess={() => {
            setShowOptInSuccess(true)
            setShowForm(false)
            setFormStartedFrom(null)
          }}
          onOptOutSuccess={() => {
            setShowOptOutSuccess(true)
            setShowForm(false)
            setFormStartedFrom(null)
          }}
          originalStatus={formStartedFrom}
          onCancel={() => {
            setShowForm(false)
            if (formStartedFrom === 'opted_in') {
              setShowOptInSuccess(true)
            } else if (formStartedFrom === 'opted_out') {
              setShowOptOutSuccess(true)
            }
            setFormStartedFrom(null)
          }}
        />
      </>
    )
  }

  if (status === 'expired') {
    return (
      <StatusConfirmation
        planYear={planYear}
        status='expired'
        selfReportedAleStatus={confirmedAleStatus ?? statusData?.self_reported_ale_status}
        onChangeStatus={() => {}}
        needsAleConfirmation={needsAleConfirmation}
        onAleConfirmed={(aleStatus) => setConfirmedAleStatus(aleStatus)}
      />
    )
  }

  if (showOptInSuccess || status === 'opted_in') {
    return (
      <StatusConfirmation
        planYear={planYear}
        status='opted_in'
        selfReportedAleStatus={statusData?.self_reported_ale_status}
        onChangeStatus={() => {
          setShowForm(true)
          setFormStartedFrom('opted_in')
          setShowOptInSuccess(false)
        }}
        needsAleConfirmation={false}
      />
    )
  }

  if (showOptOutSuccess || status === 'opted_out') {
    return (
      <StatusConfirmation
        planYear={planYear}
        status='opted_out'
        selfReportedAleStatus={statusData?.self_reported_ale_status}
        onChangeStatus={() => {
          setShowForm(true)
          setFormStartedFrom('opted_out')
          setShowOptOutSuccess(false)
        }}
        needsAleConfirmation={false}
      />
    )
  }

  return (
    <>
      <ACAReportingHeader planYear={planYear} isFilingEligible />

      <OptInForm
        planYear={planYear}
        onOptInSuccess={() => setShowOptInSuccess(true)}
        onOptOutSuccess={() => setShowOptOutSuccess(true)}
        originalStatus={null}
      />
    </>
  )
}
